import React, { useEffect, useMemo, useState } from "react";
import Moment from "moment-timezone";
import { css, select as $, media } from "glamor";

import Calendar from "../../calendar/Calendar2";
import InputField from "../../toolBox/InputField";

import { colors } from "../../../helpers/styles";

import { formatISOasAMPM } from "../../../helpers/functions";
import OutsideAlerter from "../../clickOutside/OutsideAlerter";

/*
THIS IS THE UPDATED VERSION FROM DATEPICKER.JSX
IT IS ONLY USED IN OUTLIERS FOR NOW... BUT MAY BE USED ELSEWHERE, INCLUDING LEAVE REQUESTS

IT NEEDS TO BE FURTHER REFACTORED AND TESTED, AND PROBABLY MOVED TO TOOLBOX
USE THIS COMPONENT GOING FORWARD, INSTEAD OF DATEPICKER.JSX
*/

interface MobileDayPickerProps {
  error?: string;
  focus?: boolean;
  label: string;
  value: string;
  setDate: (date: string) => void;
  type: string;
}

const MobileDayPicker: React.FC<MobileDayPickerProps> = ({
  error,
  focus,
  label,
  value,
  setDate,
  type,
}) => {
  return (
    <div {...css({ float: "left", width: "100%" })}>
      <label
        {...css({
          fontSize: 14,
          lineHeight: "17px",
          fontWeight: 500,
          color: error
            ? colors.red
            : focus
              ? colors.blue
              : "rgba(0, 0, 0, 0.54)",
        })}
      >
        {label}
      </label>
      <div {...css({ position: "relative" })}>
        <input
          autoComplete="off"
          type={type}
          value={value}
          onChange={(e) => {
            setDate(e.target.value);
          }}
          {...css(
            {
              float: "left",
              border: 0,
              borderBottom: error
                ? `1px solid ${colors.red}`
                : "1px solid rgba(226, 226, 226, 1)",
              fontFamily: "Museo Sans",
              fontSize: 16,
              color: "#28AAFF",
              textShadow: "0px 0px 0px rgba(0, 0, 0, 0.87)",
              WebkitTextFillColor: "transparent",
              width: "100%",
              height: 33,
              fontWeight: 300,
              background: "white",
            },
            $("::-webkit-input-placeholder", {
              textShadow: "none",
              WebkitTextFillColor: "initial",
            }),
            $(":focus", {
              outline: 0,
              borderBottom: error
                ? `1px solid ${colors.red}`
                : `1px solid ${colors.blue}`,
            }),
            $(":disabled", {
              background: "white",
            })
          )}
        />
      </div>
    </div>
  );
};

interface DayPickerOutsideProps {
  selectDay: (date: Date) => void;
  initialDate: Date;
  calendarStyle?: React.CSSProperties;
  handleFocus: () => void;
}

const DayPickerOutside: React.FC<DayPickerOutsideProps> = ({
  selectDay,
  initialDate,
  calendarStyle,
  handleFocus,
}) => {
  return (
    <OutsideAlerter action={handleFocus}>
      <div
        {...css(
          {
            position: "absolute",
            width: 342,
            height: 302,
            background: "white",
            textAlign: "center",
            zIndex: 1,
            borderRadius: 5,
            boxShadow: "0px 1px 4px rgba(0, 0, 0, 0.25)",
            padding: 8,
            boxSizing: "border-box",
            top: 56,
          },
          calendarStyle
        )}
      >
        <Calendar
          initialDate={new Date(initialDate)}
          specificDaysSelected={[new Date(initialDate)]}
          setSpecificDay={(date: Date) => {
            selectDay(date);
          }}
        />
      </div>
    </OutsideAlerter>
  );
};

const CalenderPicker = DayPickerOutside;

interface DayPickerProps {
  customStyle?: React.CSSProperties;
  label: string;
  value: string;
  setDate: (date: Date, type?: string) => void;
  error?: boolean;
  disabled?: boolean;
  calendarStyle?: React.CSSProperties;
}

interface DayPickerState {
  picker: boolean;
}

class DayPicker extends React.Component<DayPickerProps, DayPickerState> {
  state: DayPickerState = {
    picker: false,
  };

  render() {
    const {
      customStyle,
      label,
      value,
      setDate,
      error,
      disabled,
      calendarStyle,
    } = this.props;

    return (
      <div {...css(customStyle, { position: "relative" })}>
        <InputField
          label={label}
          value={value}
          noTyping={true}
          handleClick={() => this.setState({ picker: true })}
          error={error}
          disabled={disabled}
          externalFocus={this.state.picker}
          cursor="pointer"
        />
        {this.state.picker && (
          <CalenderPicker
            handleFocus={() => this.setState({ picker: false })}
            selectDay={(date: Date) => {
              setDate(date);
              this.setState({ picker: false });
            }}
            initialDate={new Date(value)}
            calendarStyle={calendarStyle}
          />
        )}
      </div>
    );
  }
}

interface TimeInputProps {
  value: string;
  label: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  disabled?: boolean;
  format12h?: boolean;
  error?: string | null;
}

const TimeInput: React.FC<TimeInputProps> = ({
  value,
  label,
  onChange,
  onBlur,
  disabled,
  format12h,
  error,
}) => {
  const [isFocused, setIsFocused] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);

  // Generate time slots for dropdown
  const timeSlots = useMemo(() => {
    const slots = [];
    if (format12h) {
      // 12-hour format: 12:00am, 12:30am, 1:00am, ..., 11:30pm
      for (let hour = 0; hour < 24; hour++) {
        for (let minute = 0; minute < 60; minute += 30) {
          const time = Moment().hour(hour).minute(minute);
          slots.push(time.format("h:mma"));
        }
      }
    } else {
      // 24-hour format: 00:00, 00:30, 01:00, ..., 23:30
      for (let hour = 0; hour < 24; hour++) {
        for (let minute = 0; minute < 60; minute += 30) {
          const time = Moment().hour(hour).minute(minute);
          slots.push(time.format("HH:mm"));
        }
      }
    }
    return slots;
  }, [format12h]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange(e.target.value);
  };

  const handleBlur = () => {
    setIsFocused(false);
    if (onBlur) {
      onBlur();
    }
  };

  const handleFocus = () => {
    setIsFocused(true);
  };

  const handleDropdownClick = () => {
    if (!disabled) {
      setShowDropdown(!showDropdown);
    }
  };

  const handleTimeSelect = (time: string) => {
    onChange(time);
    if (onBlur) {
      onBlur();
    }
  };

  return (
    <div {...css({ float: "left", position: "relative", width: "100%" })}>
      <label
        {...css({
          fontSize: 14,
          lineHeight: "17px",
          fontWeight: 500,
          color: error
            ? colors.red
            : isFocused
              ? colors.blue
              : "rgba(0, 0, 0, 0.54)",
        })}
      >
        {label}
      </label>
      <div {...css({ position: "relative" })}>
        <input
          autoComplete="off"
          type="text"
          value={value}
          onChange={handleInputChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onClick={handleDropdownClick}
          disabled={disabled}
          placeholder={format12h ? "2:30pm" : "14:30"}
          {...css(
            {
              float: "left",
              border: 0,
              borderBottom: error
                ? `1px solid ${colors.red}`
                : isFocused
                  ? `1px solid ${colors.blue}`
                  : "1px solid rgba(226, 226, 226, 1)",
              fontFamily: "Museo Sans",
              fontSize: 16,
              color: "rgba(0,0,0,0.87)",
              width: "100%",
              height: 33,
              fontWeight: 300,
              background: "white",
              boxSizing: "border-box",
            },
            $(":focus", {
              outline: 0,
            }),
            $(":disabled", {
              background: "white",
              cursor: "not-allowed",
            })
          )}
        />
      </div>

      {showDropdown && (
        <OutsideAlerter
          action={() => {
            setShowDropdown(false);
          }}
        >
          <div
            {...css({
              position: "absolute",
              width: "100%",
              background: "white",
              borderRadius: "5px",
              zIndex: 1,
              color: "rgba(0,0,0,0.87)",
              boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.25)",
              top: 54,
              left: 0,
              fontWeight: 300,
              textAlign: "center",
            })}
          >
            <ul
              {...css(
                {
                  float: "left",
                  width: "100%",
                  listStyle: "none",
                  padding: 6,
                  boxSizing: "border-box",
                  margin: 0,
                  maxHeight: 200,
                  overflowY: "scroll",
                },
                $("::-webkit-scrollbar", {
                  display: "none",
                })
              )}
              onScroll={(e) => e.stopPropagation()}
            >
              {timeSlots.map((time, index) => (
                <li
                  key={index}
                  {...css(
                    {
                      float: "left",
                      width: "100%",
                      boxSizing: "border-box",
                      padding: "3px 6px",
                      lineHeight: "20px",
                      fontSize: 14,
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      cursor: "pointer",
                    },
                    $(":hover", {
                      background: "rgba(0, 0, 0, 0.05)",
                      borderRadius: 3,
                      fontWeight: 500,
                    })
                  )}
                  onClick={() => handleTimeSelect(time)}
                >
                  {time}
                </li>
              ))}
            </ul>
          </div>
        </OutsideAlerter>
      )}

      {error && (
        <div
          {...css({
            color: colors.red,
            fontSize: 12,
            marginTop: 4,
            lineHeight: "14px",
            fontWeight: 500,
          })}
        >
          {error}
        </div>
      )}
    </div>
  );
};

function getNewDate(
  time: string,
  date: Date,
  format12h: boolean
): Moment.Moment | null {
  if (!time || time.trim() === "") {
    return null;
  }

  let hours: number;
  let minutes: number;

  // Handle 24-hour format (HH:mm)
  if (
    time.includes(":") &&
    !time.toLowerCase().includes("am") &&
    !time.toLowerCase().includes("pm")
  ) {
    const parts = time.split(":");
    if (parts.length === 2) {
      hours = parseInt(parts[0], 10);
      minutes = parseInt(parts[1], 10);

      if (
        isNaN(hours) ||
        isNaN(minutes) ||
        hours < 0 ||
        hours > 23 ||
        minutes < 0 ||
        minutes > 59
      ) {
        return null;
      }
    } else {
      return null;
    }
  }
  // Handle 12-hour format with AM/PM
  else if (
    time.toLowerCase().includes("am") ||
    time.toLowerCase().includes("pm")
  ) {
    try {
      // Try to parse with moment using various formats
      const timeStr = time.toLowerCase().replace(/\s/g, "");
      let parsedTime;

      // Try different 12-hour formats
      const formats = ["h:mma", "h:mm a", "ha", "h a", "h:ma", "h:m a"];

      for (const format of formats) {
        parsedTime = Moment(timeStr, format, true);
        if (parsedTime.isValid()) {
          break;
        }
      }

      if (!parsedTime || !parsedTime.isValid()) {
        return null;
      }

      hours = parsedTime.hours();
      minutes = parsedTime.minutes();
    } catch (error) {
      return null;
    }
  }
  // Handle simple number inputs (e.g., "9" for 9:00, "1430" for 14:30)
  else {
    const cleanTime = time.replace(/[^0-9]/g, "");

    if (cleanTime.length === 1) {
      // Single digit: treat as hour
      hours = parseInt(cleanTime, 10);
      minutes = 0;
    } else if (cleanTime.length === 2) {
      // Two digits: could be hour or hour:minute
      const first = parseInt(cleanTime[0], 10);
      const second = parseInt(cleanTime[1], 10);

      if (first > 2) {
        // Likely a single hour (e.g., "9" as "90")
        hours = parseInt(cleanTime, 10);
        minutes = 0;
      } else {
        // Likely hour:minute (e.g., "14")
        hours = parseInt(cleanTime, 10);
        minutes = 0;
      }
    } else if (cleanTime.length === 3) {
      // Three digits: hour:minute (e.g., "143")
      hours = parseInt(cleanTime[0], 10);
      minutes = parseInt(cleanTime.slice(1), 10);
    } else if (cleanTime.length === 4) {
      // Four digits: hour:minute (e.g., "1430")
      hours = parseInt(cleanTime.slice(0, 2), 10);
      minutes = parseInt(cleanTime.slice(2), 10);
    } else {
      return null;
    }

    if (
      isNaN(hours) ||
      isNaN(minutes) ||
      hours < 0 ||
      hours > 23 ||
      minutes < 0 ||
      minutes > 59
    ) {
      return null;
    }
  }

  return Moment(date).set({
    hours,
    minutes,
  });
}

interface DatePickerProps {
  allDay: boolean;
  startDate: Moment.Moment;
  endDate: Moment.Moment;
  setDate: (date: Moment.Moment, type: "startDate" | "endDate") => void;
  disabled?: boolean;
  timezone: string;
  format12h?: boolean;
}

export default function DatePicker(props: DatePickerProps) {
  const { allDay, startDate, endDate, setDate, disabled, timezone, format12h } =
    props;

  // Only auto-adjust for all-day events
  useEffect(() => {
    if (allDay) {
      const newStartDate = Moment(startDate).startOf("day");
      let newEndDate = Moment(endDate).startOf("day");
      if (Moment(newEndDate).isSameOrBefore(newStartDate)) {
        newEndDate = newEndDate.add(1, "day");
      }
      setDate(newStartDate, "startDate");
      setDate(newEndDate, "endDate");
    }
  }, [allDay, startDate, endDate, setDate]);

  const isValid = useMemo(() => {
    return (
      Moment(startDate).isValid() &&
      Moment(endDate).isValid() &&
      Moment(startDate).isBefore(Moment(endDate))
    );
  }, [startDate, endDate]);

  const metaBox = {
    float: "left",
    width: "100%",
    height: 20,
    lineHeight: "20px",
    fontSize: 14,
    fontWeight: 300,
  };

  const duration = Moment.duration(Moment(endDate).diff(startDate));
  const metaText = getMetaText(duration);

  return (
    <div>
      {allDay ? (
        <div {...css({ float: "left", width: "100%", marginBottom: 24 })}>
          <div {...css({ float: "left", width: "100%" })}>
            {/* mobile date picker*/}
            <div
              {...css(
                { display: "none" },
                media("(max-width: 500px)", {
                  display: "block",
                })
              )}
            >
              <div
                {...css({
                  float: "left",
                  width: "calc(50% - 6px)",
                  marginRight: 12,
                })}
              >
                <MobileDayPicker
                  error=""
                  focus={false}
                  label="Start date"
                  value={Moment(startDate).format("YYYY-MM-DD")}
                  setDate={(date: string) => {
                    const newDate = Moment.tz(
                      Moment(date).format("DD-MM-YYYY, HH:mm"),
                      "DD-MM-YYYY, HH:mm",
                      timezone
                    );
                    setDate(newDate, "startDate");
                  }}
                  type="date"
                />
              </div>
              <div {...css({ float: "left", width: "calc(50% - 6px)" })}>
                <MobileDayPicker
                  error=""
                  focus={false}
                  label="End date"
                  value={Moment(endDate)
                    .subtract(1, "day")
                    .format("YYYY-MM-DD")}
                  setDate={(date: string) => {
                    const newDate = Moment.tz(
                      Moment(date).add(1, "day").format("DD-MM-YYYY, HH:mm"),
                      "DD-MM-YYYY, HH:mm",
                      timezone
                    );
                    setDate(newDate, "endDate");
                  }}
                  type="date"
                />
              </div>
            </div>
            <div
              {...css(
                media("(max-width: 500px)", {
                  display: "none",
                })
              )}
            >
              <DayPicker
                label="Start date"
                customStyle={{
                  ...css(
                    {
                      float: "left",
                      width: "calc(50% - 8px)",
                      marginRight: 16,
                    },
                    media("(max-width: 500px)", {
                      width: "calc(50% - 6px)",
                      marginRight: 12,
                    })
                  ),
                }}
                value={Moment(startDate).format("dddd, D MMM YYYY")}
                setDate={(date: Date) => {
                  const newDate = Moment.tz(
                    Moment(date).format("DD-MM-YYYY, HH:mm"),
                    "DD-MM-YYYY, HH:mm",
                    timezone
                  );
                  if (Moment(date).isAfter(Moment(endDate))) {
                    setDate(Moment(newDate).add(1, "day"), "endDate");
                  }
                  setDate(newDate, "startDate");
                }}
                error={!isValid}
                disabled={disabled}
                calendarStyle={{ left: 0 }}
              />
              <DayPicker
                label="End date"
                customStyle={{
                  ...css(
                    { float: "left", width: "calc(50% - 8px)" },
                    media("(max-width: 500px)", {
                      width: "calc(50% - 6px)",
                    })
                  ),
                }}
                value={Moment(endDate)
                  .subtract(1, "day")
                  .format("dddd, D MMM YYYY")}
                setDate={(date: Date) => {
                  const newDate = Moment.tz(
                    Moment(date).add(1, "day").format("DD-MM-YYYY, HH:mm"),
                    "DD-MM-YYYY, HH:mm",
                    timezone
                  );
                  if (Moment(date).isBefore(Moment(startDate))) {
                    setDate(Moment(newDate).subtract(1, "day"), "startDate");
                  }
                  setDate(newDate, "endDate");
                }}
                error={!isValid}
                disabled={disabled}
                calendarStyle={{ right: 0 }}
              />
            </div>
          </div>
          <div {...css(metaBox)}>{metaText}</div>
        </div>
      ) : (
        <div {...css({ float: "left", width: "100%", marginBottom: 24 })}>
          <div
            {...css(
              { float: "left", width: "100%", display: "none" },
              media("(max-width: 500px)", {
                display: "block",
              })
            )}
          >
            <div
              {...css({
                float: "left",
                width: "100%",
                marginBottom: 24,
              })}
            >
              <MobileDayPicker
                error=""
                focus={false}
                label="Start"
                value={startDate.format("YYYY-MM-DD[T]HH:mm")}
                setDate={(date: string) => {
                  const newDate = Moment.tz(
                    Moment(date).format("DD-MM-YYYY, HH:mm"),
                    "DD-MM-YYYY, HH:mm",
                    timezone
                  );
                  setDate(newDate, "startDate");
                }}
                type="datetime-local"
              />
            </div>
            <div
              {...css({
                float: "left",
                width: "100%",
              })}
            >
              <MobileDayPicker
                error=""
                focus={false}
                label="End"
                value={endDate.format("YYYY-MM-DD[T]HH:mm")}
                setDate={(date: string) => {
                  const newDate = Moment.tz(
                    Moment(date).format("DD-MM-YYYY, HH:mm"),
                    "DD-MM-YYYY, HH:mm",
                    timezone
                  );
                  setDate(newDate, "endDate");
                }}
                type="datetime-local"
              />
            </div>
          </div>

          <DateAndTime
            dateLabel="Start date"
            hourLabel="Start time"
            date={startDate}
            duration={duration}
            disabled={disabled}
            setDate={(date: Moment.Moment) => {
              // No auto-adjustment - let the user set what they want
              setDate(date, "startDate");
            }}
            customStyle={{ marginBottom: 24 }}
            format12h={format12h}
            key={"start" + startDate.toISOString()}
          />
          <DateAndTime
            dateLabel="End date"
            hourLabel="End time"
            date={endDate}
            duration={duration}
            disabled={disabled}
            setDate={(date: Moment.Moment) => {
              // No auto-adjustment - let the user set what they want
              setDate(date, "endDate");
            }}
            format12h={format12h}
            key={"end" + endDate.toISOString()}
          />

          <div {...css(metaBox)}>
            {!isValid && (
              <div {...css({ color: colors.red, marginBottom: 8, fontWeight: 500 })}>
                End date must be after start date
              </div>
            )}
            {metaText}
          </div>
        </div>
      )}
    </div>
  );
}

function getMetaText(duration: Moment.Duration): string {
  const days = duration.days();
  const hours = duration.hours();
  const minutes = duration.minutes();

  const parts: string[] = [];

  if (days > 0) {
    parts.push(`${days} day${days !== 1 ? "s" : ""}`);
  }

  if (hours > 0 || (days === 0 && minutes > 0)) {
    // If there are no days and some minutes, show partial hours as decimal
    if (days === 0 && minutes > 0 && hours === 0) {
      const decimalHours = +duration.asHours().toFixed(1); // Rounded to 1 decimal place
      parts.push(`${decimalHours} hour${decimalHours !== 1 ? "s" : ""}`);
      return parts.join(", ");
    } else {
      parts.push(`${hours} hour${hours !== 1 ? "s" : ""}`);
    }
  }

  if (minutes > 0 && (days > 0 || hours > 0)) {
    parts.push(`${minutes} minute${minutes !== 1 ? "s" : ""}`);
  } else if (minutes > 0 && hours === 0 && days === 0) {
    parts.push(`${minutes} minute${minutes !== 1 ? "s" : ""}`);
  }

  return parts.join(", ");
}

interface DateAndTimeProps {
  format12h?: boolean;
  dateLabel: string;
  hourLabel: string;
  date: Moment.Moment;
  duration: Moment.Duration;
  disabled?: boolean;
  customStyle?: React.CSSProperties;
  setDate: (date: Moment.Moment) => void;
}

function DateAndTime(props: DateAndTimeProps) {
  const [hours, setHours] = useState(
    props.format12h
      ? formatISOasAMPM(props.date.toISOString(), undefined)
      : Moment(props.date).format("HH:mm")
  );
  const [timeError, setTimeError] = useState<string | null>(null);
  const [isTyping, setIsTyping] = useState(false);

  const { format12h, dateLabel, hourLabel, date, disabled, customStyle } =
    props;

  useEffect(() => {
    // Only validate and update if we're not currently typing
    if (!isTyping) {
      const newDate = getNewDate(
        hours,
        props.date.toDate(),
        format12h || false
      );
      if (newDate) {
        props.setDate(newDate);
        setTimeError(null);
      } else if (hours && hours.trim() !== "") {
        // Only show error if user has actually entered something and finished typing
        setTimeError("Invalid time format");
      } else if (hours === "") {
        // If input is completely empty after user finished typing, set a default
        setHours(format12h ? "12:00am" : "00:00");
      } else {
        setTimeError(null);
      }
    }
  }, [hours, format12h, isTyping]);

  const handleTimeChange = (newValue: string) => {
    setHours(newValue);
    setIsTyping(true);

    // Clear error when user starts typing
    if (timeError) {
      setTimeError(null);
    }
  };

  const handleTimeBlur = () => {
    setIsTyping(false);
    // Validation will happen in the useEffect when isTyping becomes false
  };

  return (
    <div
      {...css(
        { float: "left", width: "100%" },
        customStyle,
        media("(max-width: 500px)", {
          display: "none",
        })
      )}
    >
      <DayPicker
        label={dateLabel}
        customStyle={{
          ...css(
            { float: "left", width: 163, marginRight: 16 },
            media("(max-width: 500px)", {
              width: 165,
              marginRight: 12,
            })
          ),
        }}
        value={Moment(date).format("dddd, D MMM YYYY")}
        setDate={(date: Date) => {
          const newDate = getNewDate(hours, date, format12h || false);
          if (newDate) {
            props.setDate(newDate);
          }
        }}
        disabled={disabled}
        calendarStyle={{ left: 0 }}
      />
      <div
        {...css(
          { float: "left", width: 88, marginRight: 16 },
          media("(max-width: 500px)", {
            width: 76,
            marginRight: 12,
          })
        )}
      >
        <TimeInput
          value={hours}
          label={hourLabel}
          onChange={handleTimeChange}
          onBlur={handleTimeBlur}
          disabled={disabled}
          format12h={format12h}
          error={timeError}
        />
      </div>
    </div>
  );
}
